import axios from 'axios';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getProducts = async () => {
  const response = await axios.get('/api/lens-products', {
    headers: getAuthHeaders()
  });
  return response.data;
};

// The Lens Stock page's product list. One entry per lens marked "Stock Int"
// in Admin → Lens Catalog, grouped by (real name, index), each already bound
// to the lens_products row that carries its SPH/CYL matrix.
export const getStockIntGroups = async () => {
  const response = await axios.get('/api/lens-catalog/stock-int-groups', {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createProduct = async (payload) => {
  const response = await axios.post('/api/lens-products', payload, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteProduct = async (productId) => {
  const response = await axios.delete(`/api/lens-products/${productId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteAllProducts = async () => {
  const response = await axios.delete('/api/lens-products/all', {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getMatrix = async ({ productId, location }) => {
  const response = await axios.get('/api/lens-stock', {
    headers: getAuthHeaders(),
    params: { productId, locationType: location?.type, locationId: location?.id }
  });
  return response.data;
};

export const applyUpdates = async ({ productId, location, updates }) => {
  const response = await axios.patch('/api/lens-stock', {
    productId,
    locationType: location.type,
    locationId: location.id,
    updates
  }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const clearLensStock = async ({ productId, location }) => {
  const response = await axios.delete('/api/lens-stock', {
    headers: getAuthHeaders(),
    data: {
      productId,
      locationType: location.type,
      locationId: location.id
    }
  });
  return response.data;
};

export const importExcel = async ({ file, location, mode }) => {
  const formData = new FormData();
  formData.append('file', file);
  if (location?.type) {
    formData.append('locationType', location.type);
  }
  if (location?.id) {
    formData.append('locationId', location.id);
  }
  if (mode) {
    formData.append('mode', mode);
  }

  const response = await axios.post('/api/lens-stock/import', formData, {
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const createLensTransferRequest = async ({ lensProductId, sph, cyl, qty, notes }) => {
  const response = await axios.post('/api/stock-transfer-requests/lenses', {
    lensProductId,
    sph,
    cyl,
    qty,
    notes
  }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Read a stock sheet and hand back its cells without writing anything, so the
// order pad can be filled from a spreadsheet and checked before it is sent.
export const previewOrderExcel = async ({ file }) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post('/api/lens-stock/import/preview', formData, {
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

// A whole order pad in one request. `cells` is [{ sph, cyl, qty }].
// The per-cell endpoint above still exists for single pulls; sending a matrix
// order through it took one round trip per correction.
export const createLensTransferRequestBatch = async ({ lensProductId, cells, notes }) => {
  const response = await axios.post('/api/stock-transfer-requests/lenses/batch', {
    lensProductId,
    cells,
    notes
  }, {
    headers: getAuthHeaders(),
    // A 300-cell order is one request now, but it is still a lot of work for
    // the server — well past axios's usual expectations.
    timeout: 180000
  });
  return response.data;
};

export const getStockTransferRequests = async (params = {}) => {
  const response = await axios.get('/api/stock-transfer-requests', {
    headers: getAuthHeaders(),
    params
  });
  return response.data;
};

// Put a reserved SPH/CYL cell back into available stock. Central and the store
// that owns the shelf can both call it; the server enforces which.
export const releaseReservedCell = async ({ lensProductId, locationId, sph, cyl, qty }) => {
  const response = await axios.post('/api/reservations/release-cell', {
    productId: lensProductId,
    locationId,
    sph,
    cyl,
    qty,
  }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const returnLensToCentral = async ({ lensProductId, sph, cyl, qty }) => {
  const response = await axios.post('/api/stock-transfers/lenses/return-to-central', {
    lensProductId,
    sph,
    cyl,
    qty
  }, {
    headers: getAuthHeaders()
  });
  return response.data;
};
